import type { PillKind } from '../../components/status';
import type {
  CollaborationPartner,
  CollaborationPartnerStatus,
  CollaborationShareInvitation
} from '../../routes/collaborationControlPlaneShared';

export const supplierCollaborationGlossary = Object.freeze({
  supplier: 'Supplier',
  workspace: 'Supplier Quality Workspace',
  sentWork: 'Sent work',
  receivedWork: 'Received work',
  configuration: 'Configuration'
});

export interface StatusPresentation {
  label: string;
  tone: PillKind;
}

export type SupplierLifecycleFilter = 'all' | 'setup' | 'active' | 'inactive';

export const supplierLifecycleFilterOptions: ReadonlyArray<{
  value: SupplierLifecycleFilter;
  label: string;
}> = [
  { value: 'all', label: 'All suppliers' },
  { value: 'setup', label: 'Setup and activation' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Suspended, deactivated or archived' }
];

export const filterSuppliersByLifecycle = (
  partners: CollaborationPartner[],
  filter: SupplierLifecycleFilter
): CollaborationPartner[] => {
  if (filter === 'all') return partners;
  if (filter === 'active') return partners.filter((partner) => partner.status === 'active');
  if (filter === 'setup') {
    return partners.filter((partner) =>
      ['draft', 'awaiting_supplier_registration', 'awaiting_activation', 'declined'].includes(partner.status)
    );
  }
  return partners.filter((partner) => ['suspended', 'deactivated', 'archived'].includes(partner.status));
};

export const partnerStatusPresentation = (status: CollaborationPartnerStatus): StatusPresentation => {
  switch (status) {
    case 'draft':
      return { label: 'Setup started', tone: 'warning' };
    case 'awaiting_supplier_registration':
      return { label: 'Awaiting supplier registration', tone: 'warning' };
    case 'awaiting_activation':
      return { label: 'Ready for activation', tone: 'warning' };
    case 'declined':
      return { label: 'Invitation declined', tone: 'warning' };
    case 'active':
      return { label: 'Active', tone: 'success' };
    case 'suspended':
      return { label: 'Suspended', tone: 'warning' };
    case 'deactivated':
      return { label: 'Deactivated', tone: 'neutral' };
    case 'archived':
      return { label: 'Archived', tone: 'neutral' };
    default:
      return { label: 'Status unavailable', tone: 'neutral' };
  }
};

export const shareStatusPresentation = (
  status: CollaborationShareInvitation['status'] | string
): StatusPresentation => {
  switch (status) {
    case 'pending':
      return { label: 'Awaiting supplier', tone: 'warning' };
    case 'accepted':
      return { label: 'In progress', tone: 'success' };
    case 'imported':
      return { label: 'Imported', tone: 'success' };
    case 'rejected':
      return { label: 'Declined', tone: 'warning' };
    case 'revoked':
      return { label: 'Revoked', tone: 'neutral' };
    case 'expired':
      return { label: 'Expired', tone: 'warning' };
    default:
      return { label: 'Status unavailable', tone: 'neutral' };
  }
};

export const receivedWorkStatusPresentation = (status: string): StatusPresentation => {
  switch (status) {
    case 'pending':
      return { label: 'Needs review', tone: 'warning' };
    case 'accepted':
      return { label: 'Accepted', tone: 'success' };
    case 'imported':
      return { label: 'In progress', tone: 'success' };
    case 'rejected':
      return { label: 'Declined', tone: 'warning' };
    case 'archived':
      return { label: 'Archived', tone: 'neutral' };
    default:
      return { label: 'Status unavailable', tone: 'neutral' };
  }
};

export const inboundRelationshipStatusPresentation = (status: string): StatusPresentation => {
  switch (status) {
    case 'pending_acceptance':
      return { label: 'Needs acceptance', tone: 'warning' };
    case 'accepted':
      return { label: 'Accepted customer', tone: 'success' };
    case 'blocked':
      return { label: 'Blocked', tone: 'warning' };
    default:
      return { label: 'Status unavailable', tone: 'neutral' };
  }
};
